export enum HubSpotTargetObjectName {
  COMPANY = '0-2',
  CONTACT = '0-1'
}

// Minimal interface for HubSpot Company - only includes properties used in search/preview/list
export interface HubspotCompany {
  id: string;
  properties: Record<string, any>; // Flexible properties object
  [key: string]: any; // Allow additional fields (createdAt, updatedAt, etc.)
}

// Minimal interface for HubSpot Contact - only includes properties used in search/preview/list
export interface HubspotContact {
  id: string;
  properties: Record<string, any>; // Flexible properties object
  [key: string]: any; // Allow additional fields (createdAt, updatedAt, etc.)
}

export interface ProspectFieldOption {
  key: string; // e.g. "prospect.domain" or "prospect.linkedin_url"
  label: string; // e.g. "Prospect Data — Domain"
}

export interface ProspectMappingOptionsResponse {
  target: string;
  prospect_sources: ProspectFieldOption[];
}

export enum NormalizeRule {
  WEBSITE = 'website',
  LINKEDIN = 'linkedin',
  NONE = 'none'
}

export enum FieldCategory {
  TEXT = 'text',
  DATE = 'date',
  DATETIME = 'datetime',
  PICKLIST = 'picklist',
  NUMBER = 'number'
}

// HubSpot company fields that can be used for searching
export enum CompanyAssociationSearchBy {
  NAME = 'name', // HubSpot company name field
  DOMAIN = 'domain' // HubSpot company domain field
}

export const ALLOWED_FIELD_CATEGORIES = new Set([
  FieldCategory.TEXT,
  FieldCategory.DATE,
  FieldCategory.DATETIME,
  FieldCategory.PICKLIST,
  FieldCategory.NUMBER
]);

export enum HubSpotMappingStep {
  PRIMARY_KEY = 'primary_key',
  SEARCH = 'search',
  ASSOCIATION = 'association',
  OTHER_FIELDS = 'other_fields'
}

export interface PicklistValue {
  value: string;
  label: string;
}

export interface HubSpotFieldForMapping {
  apiName: string;
  label: string;
  dataType: string;
  fieldCategory?: FieldCategory;
  isCreateable: boolean;
  isUpdateable: boolean;
  required?: boolean;
  picklistValues?: PicklistValue[];
}

export interface MappingField {
  source_col: string;
  target_field_api_name: string;
  use_for_search: boolean;
  normalize?: 'website' | 'linkedin' | null;
  order: number;
  allow_overwrite: boolean; // If false, field won't be updated (only used for creates)
}

export interface MappingConfig {
  target_object: HubSpotTargetObjectName;
  fields: MappingField[];
}

export interface ValidationSummary {
  date_fields?: Record<string, { valid: number; invalid: number }>;
  datetime_fields?: Record<string, { valid: number; invalid: number }>;
  picklist_fields?: Record<string, { valid: number; invalid: number }>;
  email_fields?: Record<string, { valid: number; invalid: number }>;
  phone_fields?: Record<string, { valid: number; invalid: number }>;
  number_fields?: Record<string, { valid: number; invalid: number }>;
  text_fields?: Record<string, { valid: number; invalid: number }>;
}

export interface PrimaryKeyMapping {
  source_column: string;
  target_field: string;
}

export interface FieldErrorCounts {
  missing: number; // Count of rows where this field was missing/empty
  invalid: number; // Count of rows where this field had an invalid value
}

export interface RequiredFieldValidation {
  creates: {
    valid_for_creation: number; // Count of valid creates (will proceed)
    invalid_for_creation: number; // Count of invalid creates (entire row will be dropped)
    field_errors: Record<string, FieldErrorCounts>; // Grouped by field with counts
  };
  updates: {
    valid_for_overwriting: number; // Count of valid updates (will proceed)
    invalid_for_overwriting: number; // Count of invalid updates (entire row will be dropped)
    field_errors: Record<string, FieldErrorCounts>; // Grouped by field with counts
  };
}

export interface UpsertMappedPreviewResponse {
  total_records: number;
  records_to_update: number;
  records_to_create: number;
  records_skipped: number;
  validation_summary?: ValidationSummary;
  validation: RequiredFieldValidation;
  can_proceed: boolean; // Whether sync can proceed (false if ALL creates are invalid)
  primary_key_mapping: PrimaryKeyMapping;
  company_association_suggestions?: {
    [source_value: string]: Array<{
      id: string;
      name: string;
      domain: string;
      exact_match: boolean;
    }>;
  };
}

export interface UserConfirmedSelections {
  [normalized_primary_key: string]: string | null; // string = HubSpot ID to update, null = create new record
}

export interface UserConfirmedAssociations {
  company?: {
    hubspot_field: CompanyAssociationSearchBy; // HubSpot field to search by (name or domain)
    output_column_name: string; // Internal field from automation job to use for search
    selections: {
      [source_value: string]: string | null; // Company ID or null to skip
    };
  };
}

export interface SearchResult {
  id: string;
  name?: string;
  domain?: string;
  email?: string;
  properties?: {
    [property_name: string]: any;
  };
  exact_match: boolean; // true if normalized value matches exactly
}

export interface HubSpotSearchResponse {
  results: {
    [normalized_key: string]: SearchResult[];
  };
  not_found: string[]; // List of normalized keys that had no matches
}

export interface SyncJobStatus {
  job_id: string;
  status: 'queued' | 'started' | 'finished' | 'failed';
  created_at: string; // ISO timestamp
  started_at?: string; // ISO timestamp
  ended_at?: string; // ISO timestamp
  progress?: {
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
    errors: number;
  };
  error?: string; // Only present when status is "failed"
}

export interface SyncError {
  type: 'create' | 'update';
  /** Composite keys may join multiple parts (connector-defined format). */
  primary_key: string; // Often: "source_col (target_field): value"
  error: string;
  errorCode?: string; // HubSpot error code if available
}

export interface AssociationError {
  input: {
    from: string; // Contact ID
    to: string; // Company ID
  };
  message: string;
}

export interface SyncResult {
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: SyncError[];
  associations?: {
    created: number;
    errors: AssociationError[];
  };
}
